'use client';

import type { RoomInfo } from '@/app/(pages)/results/type';
import type { UniversalSearchTabItemInfo } from '@weberus/shared-types';
import { useInitSwipeBackOption } from '@weberus/ui/hooks/use-init-swipe-back-option';
import { useEffect } from 'react';
import { useSyncCookies } from '@/app/(pages)/results/hooks/use-sync-cookies';
import { useSyncWebViewSession } from '@/app/(pages)/results/hooks/use-sync-webview-session';
import { usePageKeyInitializer } from '@/app/_shared/hooks/use-page-key-initializer';
import { useLocationManagement } from '@/app/(pages)/results/hooks/use-location-management';
import { useNormalizeQueryParams } from '@/app/(pages)/results/hooks/use-normalize-query-params';
import { UniversalSearchCategoryQueryValue } from '@/app/(pages)/results/constants';
import { cleanStorageSearchFilter, SESSION_KEY } from '@/app/_components/feature/plp-srp-filter/filter-storage';
import { navigateTo } from '@/app/_shared/utils/action-navigator';
import { useUpdateSession } from '@/app/(pages)/results/hooks/use-update-session';

interface ClientInitializerProps {
  tab?: UniversalSearchTabItemInfo;
  externalTab?: UniversalSearchTabItemInfo;
  searchParams: Record<string, string>;
  checkInDate: string;
  checkOutDate: string;
  capacityAdult: number;
  capacityChild: number;
  capacityChildAges: number[];
  globalCheckInDate: string;
  globalCheckOutDate: string;
  globalCapacityRooms: RoomInfo[];
  shouldUpdateLocalSession?: boolean;
  shouldUpdateGlobalSession?: boolean;
}

export const ClientInitializer = ({
  tab,
  externalTab,
  searchParams,
  checkInDate,
  checkOutDate,
  capacityAdult,
  capacityChild,
  capacityChildAges,
  globalCheckInDate,
  globalCheckOutDate,
  globalCapacityRooms,
  shouldUpdateLocalSession,
  shouldUpdateGlobalSession,
}: ClientInitializerProps) => {
  // 훅의 순서가 중요함
  useNormalizeQueryParams();
  usePageKeyInitializer();
  useInitSwipeBackOption();
  useLocationManagement();
  useUpdateSession({
    checkInDate,
    checkOutDate,
    capacityAdult,
    capacityChild,
    capacityChildAges,
    globalCheckInDate,
    globalCheckOutDate,
    globalCapacityRooms,
    shouldUpdateLocalSession,
    shouldUpdateGlobalSession,
  });
  useSyncWebViewSession({ category: tab?.key, searchParams });
  useSyncCookies({
    checkInDate,
    checkOutDate,
    capacityAdult,
    capacityChild,
    capacityChildAges,
    globalCheckInDate,
    globalCheckOutDate,
    globalCapacityRooms,
  });

  useEffect(() => {
    const category = tab?.key;
    if (!category) return;

    // 버티컬 트랜스퍼 활성화로 놀티켓 카테고리로 응답한 경우 앱은 웹뷰, 웹은 놀티켓 페이지로 이동하도록 설정
    const isBackForward =
      (window.performance.getEntriesByType('navigation') as PerformanceNavigationTiming[])?.[0].type === 'back_forward';

    // 뒤로가기/앞으로가기 이동이 아니고 카테고리가 외부 탭인 경우
    if (!isBackForward && externalTab) {
      navigateTo({ app: externalTab?.action.app, web: externalTab?.action.web });
      return;
    }

    // 버티컬 트랜스퍼 활성화로 진입된 category와 쿼리 파라미터의 category가 일치하는지 확인후 수정
    const urlParams = new URLSearchParams(window.location.search);
    const queryCategory = urlParams.get('category');
    if (queryCategory && queryCategory !== category) {
      urlParams.set(
        'category',
        UniversalSearchCategoryQueryValue[category as keyof typeof UniversalSearchCategoryQueryValue] ||
          UniversalSearchCategoryQueryValue.LOCAL_ACCOMMODATION,
      );
      window.history.replaceState(null, '', `${window.location.pathname}?${urlParams.toString()}`);
    }

    // 세션 스토리지에서 오래된 필터 정보 제거
    cleanStorageSearchFilter(SESSION_KEY.vertical);
  }, []);

  return null;
};
